import React, { useEffect, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, ToneChip } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput, SwitchLine } from "pages/PayRoll/Shared/statutoryUi";
import {
  ComponentsTable,
  SettingsCard,
  SettingsGrid,
  VersionDetailsCard,
  VersionFooter,
  VersionStatusBanner,
  useVersionForm,
} from "pages/PayRoll/Shared/statutoryConfigUi";
import {
  JOINER_COVER_OPTIONS,
  PERIOD_OPTIONS,
  REQUIRED_CALCULATION_CODES,
  WITHIN_CEILING_OPTIONS,
  tableFormulaOf,
  validateSettings,
} from "./esiConfigData";
import { fetchTestEmployees, saveEsiDraft } from "./esiConfigApi";
import { EsiComponentPanel } from "./EsiComponentPanel";

// Configuration tab (C1): one ESI version — eligibility ceilings,
// contribution periods, coverage-start rules and the components. Built on
// the shared statutory version parts. Saving goes through `persist` (the
// ESI API, see index.js); edits to the server draft are auto-saved.

const REQUIRED = new Set(REQUIRED_CALCULATION_CODES);

// Auto-save state of the draft: "", "saving", "saved" or "failed".
const AUTOSAVE_TEXT = {
  saving: "Saving the draft…",
  saved: "Draft saved. You can come back to it later.",
  failed: "The draft couldn't be saved automatically. Your changes are still here.",
};

export const EsiSettingsTab = ({ domain, companyId, versions, setVersions, selectedId, setSelectedId, options, persist }) => {
  const autosaveTimer = useRef(null);
  const autosaveRequest = useRef(null);
  const stopAutosave = () => {
    clearTimeout(autosaveTimer.current);
    autosaveRequest.current?.abort();
  };
  const version = useVersionForm({
    versions,
    setVersions,
    selectedId,
    setSelectedId,
    validate: validateSettings,
    persist: {
      save: (form, flags) => {
        stopAutosave();
        return persist.save(form, flags);
      },
      discard: (form) => {
        stopAutosave();
        return persist.discard(form);
      },
    },
  });
  const { form, set, setComponents, error, locked, isDraft, active } = version;
  const [panel, setPanel] = useState({ open: false, mode: "edit", kind: "CALCULATION", component: null });
  const [autosave, setAutosave] = useState("");

  // The two contribution periods together cover 12 months, so picking one
  // sets the other to start six months later (April <-> October).
  const setPeriod = (key, otherKey, value) => {
    set(key)(value);
    set(otherKey)(String(((Number(value) + 5) % 12) + 1));
  };
  const [employees, setEmployees] = useState(null);

  // A draft on the server (not the unsaved first-version template) is saved
  // 1.5 s after the last change; only formats are checked there.
  const serverDraft = isDraft && typeof form.id === "number";
  const formText = JSON.stringify(form);
  const { dirty } = version;
  useEffect(() => {
    if (!serverDraft || !dirty) return undefined;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      autosaveRequest.current?.abort();
      const request = new AbortController();
      autosaveRequest.current = request;
      setAutosave("saving");
      saveEsiDraft({ domain, companyId, form: JSON.parse(formText), signal: request.signal })
        .then(() => setAutosave("saved"))
        .catch((err) => {
          if (err.name !== "AbortError") setAutosave("failed");
        });
    }, 1500);
    return () => clearTimeout(autosaveTimer.current);
  }, [serverDraft, dirty, formText, domain, companyId]);
  useEffect(
    () => () => {
      clearTimeout(autosaveTimer.current);
      autosaveRequest.current?.abort();
    },
    [],
  );
  useEffect(() => setAutosave(""), [form.id]);

  // Employees for "Test on employee", loaded the first time a component opens.
  useEffect(() => {
    if (!panel.open || employees) return undefined;
    const request = new AbortController();
    fetchTestEmployees({ domain, companyId, signal: request.signal })
      .then(setEmployees)
      .catch((err) => {
        if (err.name !== "AbortError") setEmployees([]);
      });
    return () => request.abort();
  }, [panel.open, employees, domain, companyId]);

  const saveComponent = (component) => {
    setComponents((list) =>
      panel.mode === "add" ? [...list, component] : list.map((item) => (item.code === panel.component.code ? component : item)),
    );
    setPanel((current) => ({ ...current, open: false }));
  };

  return (
    <Mui.Stack spacing={2}>
      <VersionStatusBanner form={form} active={active} locked={locked} isDraft={isDraft} itemLabel="ESI" onOpenActive={() => setSelectedId(active.id)} />
      <VersionDetailsCard form={form} locked={locked} isDraft={isDraft} set={set} error={error} />

      <SettingsGrid columns={{ xs: "1fr", lg: "1fr 1fr" }}>
        <SettingsCard title="Eligibility Setting" info="Compared with the ESI Eligibility Wage at the start of each period">
          <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }}>
            <Field label={<>ESI Wage Ceiling (per month)<Required /></>} error={error("wage_ceiling")}>
              <PlainInput type="number" adornment="₹" value={form.wage_ceiling} onChange={set("wage_ceiling")} disabled={locked} error={error("wage_ceiling")} />
            </Field>
            <Field label="Ceiling for Persons with Disability" error={error("disability_ceiling")}>
              <PlainInput
                type="number"
                adornment="₹"
                value={form.disability_ceiling}
                onChange={set("disability_ceiling")}
                disabled={locked}
                error={error("disability_ceiling")}
                placeholder="Not used"
              />
            </Field>
          </SettingsGrid>
          <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
            Leave the disability ceiling blank if not used — the disability flag is then hidden.
          </Mui.Typography>
        </SettingsCard>

        <SettingsCard title="Contribution Periods" info="Coverage is decided at the start of each period">
          <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }}>
            <Field label={<>Period 1<Required /></>} error={error("period1_start_month")}>
              <SelectField
                value={form.period1_start_month}
                onChange={(value) => value && setPeriod("period1_start_month", "period2_start_month", value)}
                options={PERIOD_OPTIONS}
                disabled={locked}
                error={error("period1_start_month")}
              />
            </Field>
            <Field label={<>Period 2<Required /></>} error={error("period2_start_month")}>
              <SelectField
                value={form.period2_start_month}
                onChange={(value) => value && setPeriod("period2_start_month", "period1_start_month", value)}
                options={PERIOD_OPTIONS}
                disabled={locked}
                error={error("period2_start_month")}
              />
            </Field>
          </SettingsGrid>
          <Mui.Box sx={{ mt: 1 }}>
            <SwitchLine
              checked={form.continue_to_period_end}
              onChange={set("continue_to_period_end")}
              disabled={locked}
              label="Stay covered until the period ends, even if wages go above the ceiling"
            />
          </Mui.Box>
          <Mui.Divider sx={{ my: 1.5, borderColor: designColors.slate200 }} />
          <Mui.Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>Coverage starts</Mui.Typography>
            <ToneChip label="New joiners & mid-period" tone="amber" />
          </Mui.Stack>
          <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }}>
            <Field label="New joiner (wage within ceiling)">
              <SelectField
                value={form.joiner_cover_from}
                onChange={(value) => value && set("joiner_cover_from")(value)}
                options={options?.joiner_cover_from || JOINER_COVER_OPTIONS}
                disabled={locked}
              />
            </Field>
            <Field label="Wage falls within ceiling mid-period">
              <SelectField
                value={form.within_ceiling_cover_from}
                onChange={(value) => value && set("within_ceiling_cover_from")(value)}
                options={options?.within_ceiling_cover_from || WITHIN_CEILING_OPTIONS}
                disabled={locked}
              />
            </Field>
          </SettingsGrid>
        </SettingsCard>
      </SettingsGrid>

      <RequestInfoBanner body="Amounts (wage formulas, rates, rounding, low-wage limit) use the version in force for the payroll month. Coverage uses the version in force at the start of the contribution period for the whole period, so a new version's coverage settings first apply at the next period start." />

      {["CALCULATION", "CONTRIBUTION"].map((kind) => (
        <ComponentsTable
          key={kind}
          title={kind === "CALCULATION" ? "Calculation Components" : "Contribution Components"}
          info={kind === "CALCULATION" ? "Define the wage bases ESI uses" : "How each contribution is calculated from the calculation components"}
          kind={kind}
          components={form.components}
          locked={locked}
          requiredCodes={REQUIRED}
          formulaOf={tableFormulaOf}
          versionName={form.name}
          onAdd={() => setPanel({ open: true, mode: "add", kind, component: null })}
          onOpen={(component) => setPanel({ open: true, mode: "edit", kind: component.kind, component })}
          onChange={setComponents}
          showMenu={false}
        />
      ))}
      {error("components") ? <RequestInfoBanner tone="danger" body={error("components")} /> : null}

      {serverDraft && autosave ? (
        <Mui.Typography sx={{ ...designType.captionText, textAlign: "right", color: autosave === "failed" ? "#DC2626" : designColors.slate600 }}>
          {AUTOSAVE_TEXT[autosave]}
        </Mui.Typography>
      ) : null}

      <VersionFooter
        form={form}
        active={active}
        locked={locked}
        isDraft={isDraft}
        dirty={version.dirty}
        saving={version.saving}
        onCancel={version.reset}
        onSave={version.save}
        onDiscard={version.discard}
      />

      <EsiComponentPanel
        open={panel.open}
        mode={panel.mode}
        kind={panel.kind}
        component={panel.component}
        form={form}
        readOnly={locked}
        domain={domain}
        companyId={companyId}
        options={options}
        employees={employees}
        onClose={() => setPanel((current) => ({ ...current, open: false }))}
        onSave={saveComponent}
      />
    </Mui.Stack>
  );
};
